// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import React from 'react';
import '../styles/PathView.css';
import { Tooltip } from '@mui/material';
import { ArrowForwardIos, Home } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Store } from 'react-notifications-component';

interface PathButtonProps {
   text?: string;
   icon?: React.ReactNode;
   onClick?: () => void;
   tooltip?: string;
   active?: boolean;
}

interface PathViewProps {
   accountName?: string;
   projectKey?: string;
   accountNumber?: string;
   projectNumber?: string;
   caseKey?: string;
}

const PathButton: React.FC<PathButtonProps> = ({ text, icon, onClick, tooltip, active }) => {
   return (
      <Tooltip title={tooltip}>
         <button
            className={`button ${active ? 'active' : ''}`}
            onClick={onClick}
         >
            <span className='icon'>{icon}</span>
            <span className='text'>{text}</span>
         </button>
      </Tooltip>
   )
};

const PathView: React.FC<PathViewProps> = ({ accountName, projectKey, accountNumber, projectNumber, caseKey }) => {

   const navigate = useNavigate();
   const activeButton = caseKey ? 'Case' : projectKey ? 'Project' : 'Account';

   const handleHomeClick = () => {
      navigate(`/support`);
   }

   const handleAccountClick = () => {
      if (accountNumber !== "") {
         navigate(`/support/accounts/${accountNumber}`);
      } else {
         Store.addNotification({
            title: "Error!",
            message: "Account not found.",
            type: "danger",
            insert: "top",
            container: "top-right",
            animationIn: ["animate__animated", "animate__fadeIn"],
            animationOut: ["animate__animated", "animate__fadeOut"],
            dismiss: {
               duration: 5000,
               onScreen: true,
            },
         });
      }
   };

   const handleProjectClick = () => {
      if (projectNumber !== "") {
         navigate(`/support/projects/${projectNumber}`);
      } else {
         Store.addNotification({
            title: "Error!",
            message: "Project not found.",
            type: "danger",
            insert: "top",
            container: "top-right",
            animationIn: ["animate__animated", "animate__fadeIn"],
            animationOut: ["animate__animated", "animate__fadeOut"],
            dismiss: {
               duration: 5000,
               onScreen: true,
            },
         });
      }
   };

   return (
      <div className={'buttonContainer'}>
         <PathButton
            icon={<Home fontSize='medium' />}
            onClick={handleHomeClick}
            tooltip='Home'
         />
         <ArrowForwardIos fontSize='small' sx={{ margin: "-5px" }} />
         <PathButton
            text={accountName}
            onClick={handleAccountClick}
            tooltip='Account'
            active={activeButton === 'Account'}
         />
         {projectKey && (
            <>
               <ArrowForwardIos fontSize='small' sx={{ margin: "-5px" }} />
               <PathButton
                  text={projectKey}
                  onClick={handleProjectClick}
                  tooltip='Project'
                  active={activeButton === 'Project'}
               />
            </>
         )}
         {caseKey && (
            <>
               <ArrowForwardIos fontSize='small' sx={{ margin: "-5px" }} />
               <PathButton
                  text={caseKey}
                  tooltip='Case'
                  active={activeButton == 'Case'}
               />
            </>
         )}
      </div>
   );
};

export default PathView;
